import { FC, useEffect, useRef, useState } from "react";
import GlobalMap from "./global-map";
import {
  changeContinentColour,
  changeCountryColour,
  getContinentFromId,
  getContinentFromViewbox,
  getContinentViewBox,
  getIdOrParentIdOfElement,
  getCountryNameFromId,
  getTooltipPosition,
  getIdFromElementOrParent,
  changeCountriesWithRewardsColour,
  isCountryWithRewards,
  getTooltipWidthOffsetFromName,
  filterRewardCountriesByContinent,
} from "./helpers";
import {
  Continent,
  continentCodeNames,
  continentMouseEnterStyle,
  continentMouseOutStyle,
  countryHoverStyle,
  countryWithRewardsStyle,
  countryWithoutRewardsStyle,
} from "./constants";
import { Tooltip } from "./styles";
import { RewardCountry } from "./types";

const MapComponent: FC<{
  selectedContinent: string | null;
  selectedCountry: string | null;
  onCountryClick: (countryId: string) => void;
  onContinentClick: (continent: string) => void;
  rewardCountries: RewardCountry[];
  isModalOpen: boolean;
  closeModal: () => void;
}> = ({
  selectedContinent,
  onContinentClick,
  selectedCountry,
  onCountryClick,
  rewardCountries,
  isModalOpen,
  closeModal,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isZoomed = selectedContinent !== Continent.WorldMap;
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  const [hoveredContinent, setHoveredContinent] = useState<string | null>(null);
  const [divPosition, setDivPosition] = useState({ top: 0, left: 0 });
  const [firstRenderDone, setFirstRenderDone] = useState(false);

  useEffect(() => {
    const svg = ref.current?.querySelector("svg");
    const pathAndGroupElements = [...svg.querySelectorAll("path"), ...svg.querySelectorAll("g")];
    const countryElements = pathAndGroupElements.filter((el) => !!el.id);

    const setRewardsforContinent = (continent: string) => {
      const allContinents = continentCodeNames[Continent.WorldMap];
      const rewardCountriesInContinent = filterRewardCountriesByContinent(
        rewardCountries,
        continent,
      );

      changeContinentColour(allContinents, countryWithoutRewardsStyle);
      changeCountriesWithRewardsColour(rewardCountriesInContinent, countryWithRewardsStyle);
    };

    const onMouseEnter = (e: MouseEvent) => {
      if (isModalOpen) {
        return;
      }

      const target = e.target as SVGElement;
      const id = getIdOrParentIdOfElement(target);
      const continent = getContinentFromId(id);
      const countries = continentCodeNames[continent];
      const viewboxContinent = getContinentFromViewbox();

      if (!isZoomed) {
        const continentLengthOffset = getTooltipWidthOffsetFromName(continent);
        const tooltipPosition = getTooltipPosition(e, svg, continentLengthOffset);
        setHoveredContinent(continent);
        setDivPosition(tooltipPosition);
        return changeContinentColour(countries, continentMouseEnterStyle);
      }

      if (viewboxContinent !== continent || !isCountryWithRewards(id, rewardCountries)) {
        return;
      }

      if (countries?.includes(id)) {
        const country = getCountryNameFromId(id);
        const countryLengthOffset = getTooltipWidthOffsetFromName(country);
        const tooltipPosition = getTooltipPosition(e, svg, countryLengthOffset);
        setDivPosition(tooltipPosition);
        setHoveredCountry(country);
        setHoveredContinent(null);
        return changeCountryColour(id, countryHoverStyle);
      }
    };

    const onMouseOut = (e: MouseEvent) => {
      const target = e.target as SVGElement;
      const id = getIdOrParentIdOfElement(target);
      const continent = getContinentFromId(id);
      const countries = continentCodeNames[continent];
      const viewboxContinent = getContinentFromViewbox();

      if (isModalOpen) {
        return setHoveredCountry(null);
      }

      if (!isZoomed) {
        setHoveredContinent(null);
        return changeContinentColour(countries, continentMouseOutStyle);
      }

      if (viewboxContinent !== continent) {
        return;
      }

      if (countries?.includes(id)) {
        setHoveredCountry(null);

        if (isCountryWithRewards(id, rewardCountries)) {
          return changeCountryColour(id, countryWithRewardsStyle);
        }

        return changeCountryColour(id, countryWithoutRewardsStyle);
      }
    };

    const onClick = (e: MouseEvent) => {
      if (isModalOpen) {
        return closeModal();
      }
      const target = e.target as HTMLElement;
      const continent = getContinentFromId(getIdFromElementOrParent(target));

      if (getIdFromElementOrParent(target) === undefined) {
        return;
      }

      if (isZoomed) {
        const country = getIdFromElementOrParent(target);

        if (!isCountryWithRewards(country, rewardCountries) || continent !== selectedContinent) {
          return;
        }
        return onCountryClick(country);
      }

      setHoveredContinent(null);
      return changeMapView(continent);
    };

    const changeMapView = (continent: string) => {
      if (isModalOpen) {
        return;
      }

      const countries = continentCodeNames[continent];
      onContinentClick(continent);
      getContinentViewBox(continent, svg);

      if (isZoomed) {
        return setRewardsforContinent(continent);
      }

      return changeContinentColour(countries, continentMouseOutStyle);
    };

    if (!firstRenderDone) {
      getContinentViewBox(selectedContinent, svg);
      setRewardsforContinent(selectedContinent);
      changeMapView(selectedContinent);
      setFirstRenderDone(true);
    }

    changeMapView(selectedContinent);

    countryElements.forEach((country) => {
      country.addEventListener("mouseenter", onMouseEnter);
    });

    svg.addEventListener("mouseout", onMouseOut);
    svg.addEventListener("click", onClick);

    return () => {
      countryElements.forEach((country) => {
        country.removeEventListener("mouseenter", onMouseEnter);
      });

      svg.removeEventListener("mouseout", onMouseOut);
      svg.removeEventListener("click", onClick);
    };
  }, [
    isZoomed,
    onContinentClick,
    selectedContinent,
    onCountryClick,
    rewardCountries,
    isModalOpen,
    closeModal,
    firstRenderDone,
  ]);

  return (
    <div ref={ref}>
      <GlobalMap />
      {hoveredCountry || hoveredContinent ? (
        <Tooltip
          // In-line styling is significantly more performant than using styled components, in this case
          style={{ top: `${divPosition.top + 10}px` || "0", left: `${divPosition.left}px` || "0" }}
          $isModalOpen={isModalOpen}
        >
          {hoveredCountry || hoveredContinent}
        </Tooltip>
      ) : null}
    </div>
  );
};

export default MapComponent;
